import { useMemo } from "react";
import { toast } from "sonner";
import {
  useFetchCalibration,
  useSignOffCalibration,
  useUnlockCalibration,
  useSetCalibrationState,
  useToggleHidden,
} from "@/api/hooks";
import { useAuth } from "@/providers/auth-provider";
import type { BuildId, CalibrationId, CalibrationState } from "@/types/identifiers";
import type { components } from "@/api/generated/schema";

type Calibration = components["schemas"]["CalibrationResponse"];

export interface CalibrationActions {
  handleFetch: () => void;
  handleSignOff: (calId: CalibrationId) => void;
  handleUnlock: (calId: CalibrationId) => void;
  handleSetState: (calId: CalibrationId, state: CalibrationState) => void;
  handleToggleHidden: (calId: CalibrationId) => void;
  hasSignedOff: boolean;
  visibleCals: Calibration[];
  hiddenCals: Calibration[];
  isFetching: boolean;
  isSigningOff: boolean;
  isUnlocking: boolean;
  isSettingState: boolean;
  isTogglingHidden: boolean;
}

export function useCalibrationActions(
  buildId: BuildId,
  calibrations: Calibration[] | undefined,
): CalibrationActions {
  const { user } = useAuth();
  const userName = user.name;
  const fetchCal = useFetchCalibration(buildId);
  const signOff = useSignOffCalibration(buildId);
  const unlock = useUnlockCalibration(buildId);
  const setState = useSetCalibrationState(buildId);
  const toggleHidden = useToggleHidden(buildId);

  const hasSignedOff = calibrations?.some((c) => c.signed_off_at) ?? false;
  const visibleCals = useMemo(() => calibrations?.filter((c) => !c.hidden) ?? [], [calibrations]);
  const hiddenCals = useMemo(() => calibrations?.filter((c) => c.hidden) ?? [], [calibrations]);

  const handleFetch = () => {
    fetchCal.mutate(userName, {
      onSuccess: () => toast.success("Calibration fetched from NVP"),
      onError: (err) => toast.error(err.message),
    });
  };

  const handleSignOff = (calId: CalibrationId) => {
    signOff.mutate(
      { calId, signedBy: userName },
      {
        onSuccess: () => toast.success("Calibration signed off"),
        onError: (err) => toast.error(err.message),
      },
    );
  };

  const handleUnlock = (calId: CalibrationId) => {
    unlock.mutate(calId, {
      onSuccess: () => toast.success("Calibration unlocked"),
      onError: (err) => toast.error(err.message),
    });
  };

  const handleSetState = (calId: CalibrationId, state: CalibrationState) => {
    setState.mutate(
      { calId, state },
      {
        onError: (err) => toast.error(err.message),
      },
    );
  };

  const handleToggleHidden = (calId: CalibrationId) => {
    toggleHidden.mutate(calId);
  };

  return {
    handleFetch,
    handleSignOff,
    handleUnlock,
    handleSetState,
    handleToggleHidden,
    hasSignedOff,
    visibleCals,
    hiddenCals,
    isFetching: fetchCal.isPending,
    isSigningOff: signOff.isPending,
    isUnlocking: unlock.isPending,
    isSettingState: setState.isPending,
    isTogglingHidden: toggleHidden.isPending,
  };
}
